import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Mail, Instagram, Dumbbell } from "lucide-react";
import { AppShell } from "../components/Shell";
import { Avatar } from "../components/Avatar";
import { ShareCard } from "../components/ShareCard";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { useT, useLabels } from "../i18n";
import { BadgeShare } from "../components/BadgeShare";

const BG = "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1200&q=70";

export default function MatchScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const L = useLabels();
  const [match, setMatch] = useState(null);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    api.get(`/matches/${id}`).then((r) => setMatch(r.data)).catch(() => navigate("/recherche"));
  }, [id, navigate]);

  if (!match) return <AppShell><div className="min-h-screen" /></AppShell>;
  const { partner, me } = match;

  return (
    <AppShell>
      <div className="relative flex min-h-screen flex-col overflow-hidden grain" data-testid="match-page">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-rox-black/70 via-rox-black/60 to-rox-black" />
        <header className="relative flex h-14 items-center justify-center"><Logo className="text-xl" to="/recherche" /></header>

        <main className="relative mx-auto flex w-full max-w-xl flex-1 flex-col items-center px-5 pb-10 pt-8 text-center">
          <h1 className="h-display text-6xl sm:text-7xl animate-punch-in" data-testid="match-title">
            {t("match_title_a")} <span className="text-rox-orange">{t("match_title_b")}</span>{t("match_excl")}
          </h1>

          <div className="mt-10 flex items-center gap-5 animate-rise-in" style={{ animationDelay: "0.25s" }}>
            <Avatar name={me.first_name} src={me.photo_url} size={104} testId="match-avatar-me" />
            <span className="font-display text-4xl text-rox-orange">X</span>
            <Avatar name={partner.first_name} src={partner.photo_url} size={104} testId="match-avatar-partner" />
          </div>

          <p className="h-display mt-8 text-3xl animate-rise-in" style={{ animationDelay: "0.4s" }} data-testid="match-names">
            {me.first_name} + {partner.first_name}
          </p>
          <p className="mt-4 max-w-xs text-base text-rox-off/85 animate-rise-in" style={{ animationDelay: "0.5s" }}>
            {t("match_text")}
            <br />
            <span className="font-semibold text-rox-off">{match.race_name} · {L.category(partner)}</span>
          </p>
          <p className="mt-2 text-sm text-rox-muted animate-rise-in" style={{ animationDelay: "0.55s" }}>{match.score}% {t("compat")}</p>
          {match.ready && <BadgeShare match={match} />}

          <div className="mt-auto w-full space-y-3 pt-10 animate-rise-in" style={{ animationDelay: "0.65s" }}>
            {!reveal ? (
              <button data-testid="contact-button" className="btn-primary" onClick={() => setReveal(true)}>
                {t("contact_partner")} <ArrowRight size={20} />
              </button>
            ) : (
              <div className="panel space-y-2 p-4 text-left animate-rise-in" data-testid="contact-card">
                <p className="eyebrow">{t("contact_of", partner.first_name)}</p>
                <a href={`mailto:${partner.contact_email}?subject=ROXPAIR%20—%20${encodeURIComponent(match.race_name)}%20Doubles`} data-testid="contact-email" className="flex items-center gap-3 rounded-[4px] bg-rox-black px-4 py-3 text-sm transition-colors hover:text-rox-orange">
                  <Mail size={18} className="text-rox-orange" /> {partner.contact_email}
                </a>
                {partner.instagram && (
                  <a href={`https://instagram.com/${partner.instagram}`} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-3 rounded-[4px] bg-rox-black px-4 py-3 text-sm transition-colors hover:text-rox-orange">
                    <Instagram size={18} className="text-rox-orange" /> @{partner.instagram}
                  </a>
                )}
              </div>
            )}
            <ShareCard match={match} />
            <button data-testid="plan-button" className="btn-ghost border-rox-orange/60 text-rox-orange hover:bg-rox-orange/10" onClick={() => navigate(`/match/${id}/plan`)}>
              <Dumbbell size={18} /> {t("our_plan")}
            </button>
            <button data-testid="back-to-pairs" className="btn-ghost" onClick={() => navigate("/recherche")}>{t("keep_searching")}</button>
          </div>
        </main>
      </div>
    </AppShell>
  );
}
